/**
 * Kestner's torch. A rubber-armoured stock lamp, authored lying down and aimed +Z.
 *
 * ── There is no beam in this file, and that is the decision ─────────────────
 *
 * `docs/beat-8-plan.md` lists this as *"a cone and a light"*. The light is not
 * here — an `AssetDefinition` is a pure geometry factory and cannot own one — and
 * the **cone is not here either**, which is the part worth writing down.
 *
 * A visible shaft of light in air is volumetrics. This renderer has none, and
 * nothing in `src/models/` has ever used a transparent material: every surface in
 * the game comes from `surface()` and `docs/asset-conventions.md` says so. An
 * additive cone faking the shaft would be the first, it would need a material
 * path the toon pipeline does not have, and it is exactly the thing
 * `chapterCard.ts` calls a logo rather than a hand.
 *
 * So the torch is a body, and the beam is the pool it throws — a bright point
 * light carried a little ahead of the lens, in a hall that beat 8 has just put
 * into the dark. The performance of the whole sequence is that pool moving:
 * onto the fuse box at her height, then onto the floor, then along the floor the
 * way she went. See `blackoutTorch.ts`.
 */

import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'torch';
  const rubber = surface('organic', 0x24282a);
  const steel = surface('metal', 0x8d9698);
  // Not white. An unlit torch lens on the workbench that reads as a bulb already
  // switched on is a prop that has spoiled its own entrance; this is the colour
  // of cold glass over a reflector.
  const lens = surface('metal', 0xb9b09a);

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.031, 0.17, 10), rubber);
  body.name = 'torch-body';
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 0.034, -0.02);
  body.castShadow = true;
  group.add(body);

  // The grip ribs are what make a 170 mm cylinder read as a working lamp rather
  // than a pipe offcut at the distance this is ever seen from.
  for (const z of [-0.06, -0.02, 0.02]) {
    const rib = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.008, 10), rubber);
    rib.name = `torch-grip-rib-${String(z)}`;
    rib.rotation.x = Math.PI / 2;
    rib.position.set(0, 0.034, z);
    group.add(rib);
  }

  const head = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.033, 0.055, 10), steel);
  head.name = 'torch-head';
  head.rotation.x = Math.PI / 2;
  head.position.set(0, 0.034, 0.092);
  head.castShadow = true;
  group.add(head);

  const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.041, 0.041, 0.006, 10), lens);
  glass.name = 'torch-lens';
  glass.rotation.x = Math.PI / 2;
  glass.position.set(0, 0.034, 0.121);
  group.add(glass);

  // A stock lamp has a lug and a strap. It is two boxes and it is the only thing
  // that says somebody carries this around all day.
  const lug = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.02, 0.03), steel);
  lug.name = 'torch-strap-lug';
  lug.position.set(0, 0.066, -0.07);
  group.add(lug);

  seatOnOrigin(group);

  return group;
}

export const torchAsset: AssetDefinition = {
  id: 'torch',
  label: 'Stock torch',
  description: 'Rubber-armoured handheld lamp aimed +Z. The beam is a light, not geometry.',
  // MEASURED off the built mesh: width and height are the head's 90 mm barrel
  // plus the strap lug, length is lug to lens.
  footprint: { width: 0.09, height: 0.086, length: 0.221 },
  create: build,
};
